import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../common/BreadCrumb";
import TableContainer from "../../../common/table/TableContainer";
import { useQuery, useQueryClient } from "react-query";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../common/HistoryContext";
import toast from "react-hot-toast";
import { getPayements, handlePaymentAction } from "../services/paymentApi";
import "../../common/style/style.css";
import AddEditPaymentForm from "../component/addEditPayment";

const PaymentsMethodes = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Payments | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionTargetId, setActionTargetId] = useState(null);
  const [name, setName] = useState("");

  const currentuser = JSON.parse(sessionStorage.getItem("authUser"));

  const { isLoading, isError, error, data: payments } = useQuery("payments", () => getPayements());

  const tabs = ["Tous", "Actives", "Désactivés"];
  const [activeTab, setActiveTab] = useState("Tous");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredPayments = useMemo(() => {
    if (!payments) return error?.message || error || [];
    switch (activeTab) {
      case "Actives":
        return payments.filter((v) => v.active === true);
      case "Désactivés":
        return payments.filter((v) => v.active === false);
      default:
        return payments;
    }
  }, [payments, activeTab]);

  const toggleAddEditModal = useCallback((payment = null) => {
    setSelectedPayment(payment);
    setIsAddEditModalOpen((prev) => !prev);
  });

  const handleEdit = (payment) => {
    toggleAddEditModal(payment);
  };

  const handleAction = async (action, paymentId) => {
    try {
      await handlePaymentAction(action, paymentId);
      toast.success(`La méthode de paiement a été ${action}ée avec succès 🎉`);
      queryClient.invalidateQueries("payments");
      closeModal();
    } catch (error) {
      console.error(`Erreur lors de l'action ${action} du paiement :`, error?.message);
      toast.error(`😔 Erreur lors de l'${action} du paiement : ${error?.message || error}`);
    }
  };

  const openModal = (type, targetId, name) => {
    setActionType(type);
    setActionTargetId(targetId);
    setName(name);
  };

  const closeModal = () => {
    setActionType(null);
    setActionTargetId(null);
    setName('');
  };

  const columns = useMemo(
    () => [
      { Header: "Numéro", accessor: "id", filterable: false },
      { Header: "Pays", accessor: "country", filterable: false },
      { Header: "Nom", accessor: "name", filterable: false },
      {
        Header: "Statut",
        accessor: "active",
        Cell: ({ value }) => (
          <span
            className={`badge bg-${value ? "success" : "danger"}-subtle text-${value ? "success" : "danger"}`}
          >
            {value ? "Actif" : "Inactif"}
          </span>
        ),
        filterable: false,
      },      
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              title="Modifier"
              size="sm"
              onClick={() => handleEdit(row.original)}
              className="btn btn-soft-warning"
            >
              <EditOutlined />
            </Button>
            <Button
              title="Activer"
              size="sm"
              onClick={() => openModal("activate", row.original.id, row.original.name)}
              className="btn btn-soft-info"
            >
              <CheckOutlined />
            </Button>

            <Button
              title="Désactiver"
              size="sm"
              onClick={() => openModal("deactivate", row.original.id, row.original.name)}
              className="btn btn-soft-warning"
            >
              <CloseOutlined />
            </Button>
            <Button
              title="Supprimer"
              size="sm"
              onClick={() => openModal("delete", row.original.id, row.original.name)}
              className="btn btn-soft-danger"
            >
              <DeleteOutlined />
            </Button>
          </div>
        ),
        filterable: false,
      },
    ],
    [handleEdit]
  );

  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button
          key={tab}
          onClick={() => handleTabChange(tab)}
          className={`btn ${activeTab === tab ? "active-tab" : "btn-light"}`}
        >
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">{renderTabs()}</div>
      <Button
        type="button"
        className="active-tab"
        icon={<PlusCircleOutlined />}
        title="Développeurs"
        onClick={() => toggleAddEditModal(null)}
      >
        Ajouter une méthode de paiement
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Développeurs" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainer
                    columns={columns}
                    data={filteredPayments}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter... <Spin />
                    </div>
                  )}
                  {filteredPayments && filteredPayments.length === 0 && !isLoading && (
                    <div className="text-center">Aucun développeur pour ce marchand.</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <AddEditPaymentForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedPayment={selectedPayment}
            currentuser={currentuser}
          />
          <Modal
            title={`Confirmer ${actionType === "delete" ? "la suppression" : actionType === "deactivate" ? "la désactivation" : "l'activation"} de la méthode de paiement`}
            open={actionType !== null}
            onCancel={closeModal}
            footer={[
              <Button key="cancel" onClick={closeModal} className="btn btn-soft-light">
                Annuler
              </Button>,
              <Button
                key={actionType}
                type={actionType === "delete" ? "danger" : actionType === "deactivate" ? "warning" : "success"}
                className="active-tab"
                onClick={() => handleAction(actionType, actionTargetId)}
              >
                Oui, {actionType === "delete" ? "Supprimer" : actionType === "deactivate" ? "Désactiver" : "Activer"}
              </Button>,
            ]}
          >
            <p className="text-center">
              Êtes-vous sûr de vouloir {actionType === "delete" ? "supprimer" : actionType === "deactivate" ? "désactiver" : "activer"} cette méthode de paiement : {name} ?
            </p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PaymentsMethodes;
